/**
 * 간단한 Chrome PDF 안내 버전
 */
import { createRoot } from 'react-dom/client';
import React from 'react';
import BrowserPDFGuideModal from '../components/pdf/BrowserPDFGuideModal';
import { trackPDFDownload, getBrowserInfo } from './analytics/userActions';
import { savePDFDownloadInfo } from './analytics/pdfDownloadTracking';

/**
 * 간단한 모달 표시 함수
 */
const showBrowserGuide = () => {
  return new Promise((resolve) => {
    const modalContainer = document.createElement('div');
    document.body.appendChild(modalContainer);
    
    const root = createRoot(modalContainer);
    
    const cleanup = () => {
      root.unmount();
      document.body.removeChild(modalContainer);
    };
    
    const handleConfirm = () => {
      cleanup();
      resolve(true);
    };
    
    const handleCancel = () => {
      cleanup();
      resolve(false);
    };
    
    root.render(
      React.createElement(BrowserPDFGuideModal, {
        isOpen: true,
        onConfirm: handleConfirm,
        onCancel: handleCancel
      })
    );
  });
};

/**
 * 인쇄용 CSS
 */
const addPrintStyles = () => {
  const printStyles = `
    <style id="print-styles">
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        -webkit-font-smoothing: antialiased !important;
        -moz-osx-font-smoothing: grayscale !important;
      }
      
      @media print {
        body {
          margin: 0 !important;
          padding: 0 !important;
          font-family: 'Malgun Gothic', '맑은고딕', 'Apple SD Gothic Neo', sans-serif !important;
        }
        
        body * {
          visibility: hidden;
        }
        
        .resume-print-container, .resume-print-container * {
          visibility: visible;
        }
        
        .resume-print-container {
          position: absolute;
          left: 0;
          top: 0;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          box-shadow: none !important;
          border: none !important;
          outline: none !important;
        }
        
        @page {
          size: A4;
          margin: 8mm;
        }
        
        .resume-print-container {
          font-size: 14px !important;
          line-height: 1.6 !important;
          font-family: 'Malgun Gothic', '맑은고딕', 'Apple SD Gothic Neo', sans-serif !important;
          letter-spacing: -0.02em !important;
          width: 794px !important;
          min-width: 794px !important;
          max-width: 794px !important;
          min-height: 1123px !important;
        }
        
        .resume-print-container h1 {
          font-size: 1.875rem !important;
          font-weight: 700 !important;
          margin-bottom: 0.5rem !important;
        }
        
        .resume-print-container h2 {
          font-size: 1.25rem !important;
          font-weight: 700 !important;
          margin-bottom: 1rem !important;
          padding-bottom: 0.5rem !important;
        }
        
        .resume-print-container .text-blue-600 { color: #2563eb !important; }
        .resume-print-container .text-gray-900 { color: #111827 !important; }
        .resume-print-container .text-gray-700 { color: #374151 !important; }
        .resume-print-container .bg-blue-50 { background-color: #eff6ff !important; }
        .resume-print-container .border-gray-300 { border-color: #d1d5db !important; }
        
        .resume-print-container .flex { display: flex !important; }
        .resume-print-container .items-center { align-items: center !important; }
        .resume-print-container .justify-between { justify-content: space-between !important; }
        .resume-print-container .gap-2 { gap: 0.5rem !important; }
        
        .resume-print-container section {
          page-break-inside: auto;
          break-inside: auto;
        }
        
        .resume-print-container h2, .resume-print-container h3 {
          page-break-after: avoid;
          break-after: avoid;
        }
      }
    </style>
  `;
  
  const existingStyles = document.getElementById('print-styles');
  if (existingStyles) {
    existingStyles.remove();
  }
  
  document.head.insertAdjacentHTML('beforeend', printStyles);
};

/**
 * 이력서 요소 찾기
 */
const prepareResumeForPrint = () => {
  const resumeElement = document.querySelector('[data-pdf-content]') || 
                        document.querySelector('.resume-renderer-content [data-pdf-content]') ||
                        document.querySelector('.resume-renderer-content') ||
                        document.querySelector('.pdf-content') ||
                        document.querySelector('.print-container') ||
                        document.querySelector('.max-w-\\[210mm\\]') ||
                        document.querySelector('.bg-white.print-container.pdf-content');
  
  if (!resumeElement) {
    console.log('🔍 이력서 요소를 찾을 수 없습니다.');
    throw new Error('이력서 요소를 찾을 수 없습니다. 페이지가 완전히 로드되었는지 확인해주세요.');
  }
  
  const clonedElement = resumeElement.cloneNode(true);
  clonedElement.classList.add('resume-print-container');
  
  return clonedElement;
};

/**
 * 간단한 PDF 생성 함수
 */
export const printResumeAsPDF = async (resumeData, { userId, sessionId, resumeId, language, templateId } = {}) => {
  try {
    console.log('🖨️ PDF 생성 시작...');
    
    // 브라우저별 PDF 안내 모달 표시
    const userConfirmed = await showBrowserGuide();
    if (!userConfirmed) {
      console.log('사용자가 PDF 생성을 취소했습니다.');
      return false;
    }
    
    // 인쇄용 스타일 추가
    addPrintStyles();
    
    // DOM 백업
    const originalBodyChildren = Array.from(document.body.children);
    const originalBodyClass = document.body.className;
    const originalTitle = document.title;
    
    try {
      // 이력서 요소 준비
      const printElement = prepareResumeForPrint();
      
      // 기존 요소들 숨김
      originalBodyChildren.forEach(child => {
        child.style.display = 'none';
      });
      
      document.body.className = '';
      document.body.appendChild(printElement);
      
      // PDF 다운로드 추적 (사용자가 확인한 후)
      if (userId) {
        try {
          // 기존 추적 (호환성 유지)
          await trackPDFDownload({
            user_id: userId,
            session_id: sessionId,
            resume_id: resumeId,
            download_format: 'pdf',
            browser_info: getBrowserInfo()
          });
          
          // 새로운 PDF 다운로드 정보 저장 (템플릿 정보 포함)
          await savePDFDownloadInfo({
            userId,
            sessionId,
            templateId: templateId || 1, // 템플릿 ID가 없으면 기본값 1
            language: language || 'kr',
            browserInfo: getBrowserInfo()
          });
          
        } catch (trackError) {
          // 추적 실패는 조용히 처리
        }
      }
      
      // 인쇄 대화상자 열기
      setTimeout(() => {
        window.print();
        
        // 복원
        setTimeout(() => {
          try {
            document.body.removeChild(printElement);
            originalBodyChildren.forEach(child => {
              child.style.display = '';
            });
            document.body.className = originalBodyClass;
            document.title = originalTitle;
            
            const printStylesElement = document.getElementById('print-styles');
            if (printStylesElement) {
              printStylesElement.remove();
            }
            
            window.dispatchEvent(new Event('resize'));
            console.log('✅ 페이지 복원 완료');
            
          } catch (restoreError) {
            console.error('❌ 페이지 복원 중 오류:', restoreError);
            window.location.reload();
          }
        }, 1000);
      }, 500);
      
    } catch (domError) {
      console.warn('⚠️ DOM 기반 PDF 생성 실패:', domError);
      
      // 원본 복원
      originalBodyChildren.forEach(child => {
        child.style.display = '';
      });
      document.body.className = originalBodyClass;
      document.title = originalTitle;
      
      throw domError;
    }
    
    return true;
    
  } catch (error) {
    console.error('❌ PDF 생성 실패:', error);
    
    const printStylesElement = document.getElementById('print-styles');
    if (printStylesElement) {
      printStylesElement.remove();
    }
    
    throw error;
  }
};

/**
 * 메인 PDF 다운로드 함수
 */
export const downloadResumeAsPDF = async (resumeData, trackingData = {}) => {
  try {
    console.log('📄 PDF 다운로드 시작...');
    return printResumeAsPDF(resumeData, trackingData);
  } catch (error) {
    console.error('❌ PDF 다운로드 실패:', error);
    throw error;
  }
};

// 테스트 함수
if (typeof window !== 'undefined') {
  window.testBrowserPDF = async function() {
    try {
      const result = await printResumeAsPDF({ name: '테스트' });
      console.log('테스트 결과:', result);
    } catch (error) {
      console.error('테스트 실패:', error);
    }
  };
  
}